import { ZButton } from "@/shared/components/button";
import FileUpload from "@/shared/components/custom/file-upload";
import { ZModal, ZModalBody, ZModalContent, ZModalHeader } from "@/shared/components/modal";
import { UploadedFile } from "@/shared/types/file-upload";
import type { FC } from "react";
import { useState } from "react";

interface AutoFillWithAIModalProps {
  /**
   * Handler called when the user confirms to generate responses
   */
  onConfirm: () => void;

  /**
   * Handler called when the modal is closed without generating
   */
  onClose: () => void;

  /**
   * Whether the modal is open or not
   */
  isOpen: boolean;

  /**
   * Handler for file upload
   */
  onFileUpload?: (files: UploadedFile[]) => void;

  /**
   * Whether the generation is in progress
   */
  isGenerating?: boolean;
}

const AutoFillWithAIModal: FC<AutoFillWithAIModalProps> = ({
  onConfirm,
  onClose,
  isOpen,
  onFileUpload,
  isGenerating = false,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const handleFileChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
    if (onFileUpload) {
      onFileUpload(files);
    }
  };

  const hasValidFiles = uploadedFiles.some(file => file.status === "success");
  const hasFileUploadError = uploadedFiles.some(file =>
    ["duplicate_files", "too_large", "too_many_files", "failed"].includes(file.status)
  );

  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={onClose}
      closeButton={false}
      hideCloseButton={true}
    >
      <ZModalContent className="w-[512px] max-w-[512px] h-[682px] bg-white shadow-[0px_0px_24px_rgba(0,0,0,0.05)] rounded-[16px] p-6 flex flex-col">
        <ZModalHeader className="flex flex-col w-full items-center pb-0 mb-6">
          <p className="text-[18px] font-semibold text-[#11181C] leading-[28px]">Auto-fill with AI</p>
        </ZModalHeader>
        <ZModalBody className="p-0 mb-8 flex-1">
          <div className="flex flex-col gap-6 h-full">
            {/* Description */}
            <div className="flex flex-col items-center gap-2">
              <p className="text-[14px] text-[#777780] text-center leading-[20px] max-w-[464px]">
                Upload source files for Zania to auto-populate answers. It will only add high-confidence responses and may overwrite existing ones.
              </p>
              <p className="text-[14px] text-[#F31260] text-center leading-[20px] max-w-[464px]">
                Note: Uploads are private and not shared with the assessor.
              </p>
            </div>

            {/* File Upload */}
            <div className="flex flex-col gap-1.5 flex-1">
              <FileUpload
                isMultiple={true}
                maxFiles={50}
                allowedFileTypes={[
                  "text/csv",
                  "application/pdf",
                  "application/msword",
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  "application/vnd.ms-excel",
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  "image/jpeg",
                  "image/png",
                  "application/json"
                ]}
                setFiles={handleFileChange}
                maxSizeMB={20}
              />
            </div>
          </div>
        </ZModalBody>

        {/* Footer Container */}
        <div className="flex flex-col gap-4 mt-auto">
          {/* Separator */}
          <div className="w-full h-0 border-t border-[#DCDCDC]" />

          {/* Buttons */}
          <div className="flex justify-end items-center gap-4">
            <ZButton
              variant="bordered"
              className="px-3 py-1 text-[14px] font-medium h-[28px] border-[#006FEE] text-[#006FEE] bg-white hover:bg-[#006FEE] hover:text-white"
              onClick={onClose}
              isDisabled={isGenerating}
            >
              Cancel
            </ZButton>
            <ZButton
              className="px-3 py-1 text-[14px] font-medium h-[28px] bg-[#006FEE] text-white border-none rounded-[6px] hover:bg-[#0056CC]"
              onClick={onConfirm}
              isDisabled={!hasValidFiles || hasFileUploadError || isGenerating}
              isLoading={isGenerating}
            >
              Generate Responses
            </ZButton>
          </div>
        </div>
      </ZModalContent>
    </ZModal>
  );
};

export default AutoFillWithAIModal;
